import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  Modal,
  message,
  Tag,
  Space,
  Form,
  Select,
  InputNumber,
  Spin,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Drawer,
  Input,
  Empty,
} from "antd";
import {
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  WarningOutlined,
  BookOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  VideoCameraOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  QuestionOutlined,
  FilePdfOutlined,
  SaveOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { jwtDecode } from "jwt-decode";
import withRoleAccess from "../../hoc/withRoleAccess";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import CourseSchedulePdfModal from './CourseSchedulePdfModal';
import './CoursePage.css';

const { confirm } = Modal;
const { Option } = Select;
const { Title, Text } = Typography;

const CoursePage = () => {
  const { groupid } = useParams();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const [course, setCourse] = useState(null);
  const [classes, setClasses] = useState([]);
  const [attendanceHours, setAttendanceHours] = useState({
    presentMinutes: 0,
    absentMinutes: 0,
    lateMinutes: 0,
    toBeRescheduledMinutes: 0,
    totalMinutes: 0,
  });
  const [attendanceByClass, setAttendanceByClass] = useState({});
  const [registeredStudents, setRegisteredStudents] = useState([]);
  const [attendanceDrawerOpen, setAttendanceDrawerOpen] = useState(false);
  const [attendanceClass, setAttendanceClass] = useState(null);
  const [attendanceDraft, setAttendanceDraft] = useState({});
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [form] = Form.useForm();
  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : {};
  const userRole = decoded.role;
  // Admin-confirmed classes: only a superadmin may change date/time/hours
  const isScheduleLocked = (record) =>
    record.adminapprove === "adminconfirm" && userRole !== "superadmin";
  const LOCKED_TITLE = "Admin confirmed — only superadmin can change";
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);

  // ✨ NEW: Inline editing states
  const [editingTeacherId, setEditingTeacherId] = useState(null);
  const [editedTeacherId, setEditedTeacherId] = useState(null);
  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const [editedSubjectId, setEditedSubjectId] = useState(null);
  const [editingDateId, setEditingDateId] = useState(null);
  const [editedDate, setEditedDate] = useState('');
  const [editingTimeId, setEditingTimeId] = useState(null);
  const [editedTime, setEditedTime] = useState('');
  const [editingHourId, setEditingHourId] = useState(null);
  const [editedHour, setEditedHour] = useState(null);
  // ✨ NEW: Inline editing for Platform
  const [editingPlatformId, setEditingPlatformId] = useState(null);
  const [editedPlatform, setEditedPlatform] = useState(null);
  const [pdfPreviewVisible, setPdfPreviewVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pendingChange, setPendingChange] = useState(null); // { classid, field, value, originalRecord }

  // ✨ NEW: Bulk edit (teacher / subject) for the selected classes
  const [bulkEditField, setBulkEditField] = useState(null); // 'teacher_id' | 'subjectid'
  const [bulkEditValue, setBulkEditValue] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  // ✨ NEW: Bulk attendance for the selected classes
  const [bulkAttendanceOpen, setBulkAttendanceOpen] = useState(false);
  const [bulkAttendanceStatus, setBulkAttendanceStatus] = useState(undefined);
  const [bulkAttendanceHomework, setBulkAttendanceHomework] = useState(undefined); // undefined = leave as-is
  const [bulkAttendanceComment, setBulkAttendanceComment] = useState('');
  const [bulkAttendanceMode, setBulkAttendanceMode] = useState('overwrite'); // 'overwrite' | 'fill'
  const [bulkAttendanceStudentIds, setBulkAttendanceStudentIds] = useState([]);
  const [bulkAttendanceSaving, setBulkAttendanceSaving] = useState(false);

  // Platform modal
  const [platformModalVisible, setPlatformModalVisible] = useState(false);
  const [selectedCoursePlatform, setSelectedCoursePlatform] = useState(null);

  // Format minutes to hours:minutes
const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return "0:00 hr";

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return `${hours}:${mins.toString().padStart(2, '0')} hr`;
};


  // Time options 08:00–23:00 every 15 mins
  const timeOptions = Array.from({ length: (23 - 8 + 1) * 4 }, (_, index) => {
    const hour = 8 + Math.floor(index / 4);
    const minutes = (index % 4) * 15;
    const label = `${hour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    return { label, value: label };
  });

  // Duration options 15min → 3 hours (in minutes)
  const durationOptions = Array.from({ length: 12 }, (_, index) => {
    const minutes = (index + 1) * 15;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return {
      label: `${h}h ${m.toString().padStart(2, "0")}m`,
      value: minutes,
    };
  });

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      try {
        const [teachersRes, subjectsRes] = await Promise.all([
          axios.get(`${API_URL}/teachers`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/subjects`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        setTeachers(teachersRes.data.teachers || []);
        setSubjects(subjectsRes.data || []);
      } catch (error) {
        console.error('Error fetching teachers or subjects:', error);
      }
    };
    fetchData();
  }, [API_URL]);

 const fetchCourseData = async () => {
  try {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    const [res, hoursRes, recordsRes, studentsRes] = await Promise.all([
      axios.get(`${API_URL}/class-groups/${groupid}`, { headers }),
      axios.get(`${API_URL}/attendance/group/${groupid}/hours`, { headers })
        .catch((err) => {
          console.error('Attendance hours fetch failed (is the backend restarted?):', err.response?.status, err.response?.data);
          return { data: null };
        }),
      axios.get(`${API_URL}/attendance/group/${groupid}/records`, { headers })
        .catch((err) => {
          console.error('Attendance records fetch failed (is the backend restarted?):', err.response?.status, err.response?.data);
          return { data: [] };
        }),
      axios.get(`${API_URL}/registrations/classgroup/${groupid}`, { headers })
        .catch((err) => {
          console.error('Registered students fetch failed:', err.response?.status, err.response?.data);
          return { data: [] };
        }),
    ]);

    setCourse(res.data.course || res.data);
    setClasses(res.data.classes || []);
    if (hoursRes.data) {
      setAttendanceHours(hoursRes.data);
    }
    const map = {};
    (recordsRes.data || []).forEach((r) => {
      if (!map[r.classid]) map[r.classid] = [];
      map[r.classid].push(r);
    });
    setAttendanceByClass(map);
    setRegisteredStudents(studentsRes.data || []);
  } catch (err) {
    console.error("❌ [fetchCourseData] Error:", err);
    message.error("Failed to load course details.");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchCourseData();
  }, [groupid]);

  // ✨ NEW: Inline edit handlers for Teacher
  const handleEditTeacherClick = (classid, teacherId) => {
    setEditingTeacherId(classid);
    setEditedTeacherId(teacherId);
  };

  const handleSaveTeacherClick = (classid) => {
    const record = classes.find(c => c.classid === classid);
    handleChangeSubmit(undefined, undefined, { classid, field: 'teacher_id', value: editedTeacherId, originalRecord: record });
  };

  // ✨ NEW: Inline edit handlers for Subject
  const handleEditSubjectClick = (classid, subjectId) => {
    setEditingSubjectId(classid);
    setEditedSubjectId(subjectId);
  };

  const handleSaveSubjectClick = (classid) => {
    const record = classes.find(c => c.classid === classid);
    handleChangeSubmit(undefined, undefined, { classid, field: 'subjectid', value: editedSubjectId, originalRecord: record });
  };

  // ✨ NEW: Inline edit handlers for Date
  const handleEditDateClick = (classid, date) => {
    setEditingDateId(classid);
    setEditedDate(moment(date).format('YYYY-MM-DD'));
  };

  const handleSaveDateClick = (classid) => {
    const record = classes.find(c => c.classid === classid);
    handleChangeSubmit(undefined, undefined, { classid, field: 'date', value: editedDate, originalRecord: record });
  };

  // ✨ NEW: Inline edit handlers for Time
  const handleEditTimeClick = (classid, time) => {
    setEditingTimeId(classid);
    setEditedTime(moment(time, "HH:mm:ss").format("HH:mm"));
  };

  const handleSaveTime = (classid) => {
    const record = classes.find(c => c.classid === classid);
    handleChangeSubmit(undefined, undefined, { classid, field: 'schedule_time', value: editedTime, originalRecord: record });
  };

  // ✨ NEW: Inline edit handlers for Duration
  const handleEditHourClick = (classid, hour) => {
    setEditingHourId(classid);
    setEditedHour(hour);
  };

  const handleSaveHour = (classid) => {
    const record = classes.find(c => c.classid === classid);
    handleChangeSubmit(undefined, undefined, { classid, field: 'schedule_hour', value: editedHour, originalRecord: record });
  };

  // ✨ NEW: Start editing Platform
const handleEditPlatformClick = (classid, platform) => {
  setEditingPlatformId(classid);
  setEditedPlatform(platform);
};

// ✨ NEW: Save Platform
const handleSavePlatformClick = (classid) => {
  const record = classes.find(c => c.classid === classid);
  handleChangeSubmit(undefined, undefined, { classid, field: 'platform', value: editedPlatform, originalRecord: record });
};

// ✨ NEW: Apply one class change (PUT + change log) with no UI side effects,
// so it can be reused for a single inline edit or for a bulk update.
const applyClassChange = async (change, reason, note) => {
  const { classid, field, value, originalRecord } = change;

  // Build the PUT body based on field
  const bodyMap = {
    teacher_id: { teacher_id: value },
    subjectid: { subjectid: value },
    date: { date: value },
    schedule_time: { schedule_time: value },
    schedule_hour: { schedule_hour: value },
    platform: { platform: value },
  };

  await axios.put(
    `${API_URL}/classes/${classid}`,
    bodyMap[field],
    { headers: { Authorization: `Bearer ${token}` } }
  );

  // Parse schedule_hour which may be "1h 30m" string or raw minutes
  const parseHourToMinutes = (h) => {
    if (typeof h === 'number') return h;
    if (typeof h === 'string') {
      const match = h.match(/(\d+)h\s*(\d+)m/);
      if (match) return parseInt(match[1]) * 60 + parseInt(match[2]);
      return parseInt(h) || 0;
    }
    return 0;
  };

  // Calculate end time for logging
  const scheduleTime = field === 'schedule_time' ? value : originalRecord.schedule_time;
  const origHourMins = parseHourToMinutes(originalRecord.schedule_hour);
  const newHourMins = field === 'schedule_hour' ? parseHourToMinutes(value) : origHourMins;

  const originalDate = originalRecord.date ? moment(originalRecord.date).format('YYYY-MM-DD') : null;
  const originalStartTime = originalRecord.schedule_time || null;
  const originalEndTime = moment(originalRecord.schedule_time, 'HH:mm:ss').add(origHourMins, 'minutes').format('HH:mm:ss');

  const newDate = field === 'date' ? value : originalDate;
  const newStartTime = field === 'schedule_time' ? value : originalStartTime;
  const newEndTime = moment(scheduleTime, 'HH:mm:ss').add(newHourMins, 'minutes').format('HH:mm:ss');

  // Log the change
  await axios.post(
    `${API_URL}/class-change/newlogs`,
    {
      classid,
      teacherid: field === 'teacher_id' ? value : originalRecord.teacher_id,
      originaldate: originalDate,
      originalstarttime: originalStartTime,
      originalendtime: originalEndTime,
      newdate: newDate,
      newstarttime: newStartTime,
      newendtime: newEndTime,
      changereason: reason || 'class_changed',
      note: note || `Changed ${field}`,
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );
};

const handleChangeSubmit = async (reason, note, change = pendingChange) => {
  if (!change) return;

  try {
    await applyClassChange(change, reason, note);

    message.success("Updated successfully");

    // Reset editing states
    setEditingTeacherId(null);
    setEditingSubjectId(null);
    setEditingDateId(null);
    setEditingTimeId(null);
    setEditingHourId(null);
    setEditingPlatformId(null);
    setPendingChange(null);
    fetchCourseData();
  } catch (error) {
    console.error("Error updating class:", error);
    message.error(error.response?.data?.message || "Failed to update");
  }
};

// ✨ NEW: Apply the picked teacher / subject to every selected class
const handleBulkEditSubmit = async () => {
  if (!bulkEditField) return;
  if (bulkEditValue === null || bulkEditValue === undefined) {
    message.warning("Please choose a value first");
    return;
  }

  const label = bulkEditField === 'teacher_id' ? 'teacher' : 'subject';
  setBulkSaving(true);

  const results = await Promise.allSettled(
    selectedRowKeys.map((classid) => {
      const record = classes.find((c) => c.classid === classid);
      if (!record) return Promise.reject(new Error(`Class ${classid} not found`));
      return applyClassChange(
        { classid, field: bulkEditField, value: bulkEditValue, originalRecord: record },
        undefined,
        `Bulk changed ${label}`
      );
    })
  );

  setBulkSaving(false);

  const failed = results.filter((r) => r.status === "rejected");
  if (failed.length > 0) {
    console.error("❌ [handleBulkEditSubmit] Failures:", failed.map((f) => f.reason));
    message.error(
      `${results.length - failed.length} of ${results.length} classes updated — ${failed.length} failed`
    );
  } else {
    message.success(`${label} updated on ${results.length} classes`);
    setSelectedRowKeys([]);
  }

  setBulkEditField(null);
  setBulkEditValue(null);
  fetchCourseData();
};

  const handleDeleteClass = (classid) => {
    confirm({
      title: "Delete Class",
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure you want to delete this class from the course?",
      okText: "Delete",
      cancelText: "Cancel",
      okType: "danger",
      async onOk() {
        try {
          const url = `${API_URL}/classes/${classid}`;
          await axios.delete(url, {
            headers: { Authorization: `Bearer ${token}` },
            data: { password: "confirm" },
          });

          message.success("Class deleted successfully");
          fetchCourseData();
        } catch (error) {
          console.error("❌ [handleDeleteClass] Error:", error);
          message.error(error.response?.data?.message || "Failed to delete class");
        }
      },
    });
  };

  const handleBulkDelete = () => {
    if (selectedRowKeys.length === 0) return;
    confirm({
      title: `Delete ${selectedRowKeys.length} Classes`,
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete ${selectedRowKeys.length} selected classes?`,
      okText: "Delete All Selection",
      cancelText: "Cancel",
      okType: "danger",
      async onOk() {
        try {
          await Promise.all(
            selectedRowKeys.map((classid) =>
              axios.delete(`${API_URL}/classes/${classid}`, {
                headers: { Authorization: `Bearer ${token}` },
                data: { password: "confirm" },
              })
            )
          );
          message.success(`${selectedRowKeys.length} classes deleted successfully`);
          setSelectedRowKeys([]);
          fetchCourseData();
        } catch (error) {
          console.error("❌ [handleBulkDelete] Error:", error);
          message.error("Failed to delete some classes");
        }
      },
    });
  };

  const handleAddClass = async () => {
    try {
      const values = await form.validateFields();

      const classgroup_id = Array.isArray(course.classgroup_ids)
        ? course.classgroup_ids[0]
        : course.classgroup_ids || course.classgroup_id;

      if (!classgroup_id) {
        console.error("❌ Missing classgroup_id in course object:", course);
        message.error("Missing classgroup_id — cannot add class.");
        return;
      }

      const durationMinutes = parseInt(values.duration, 10) || 0;
      const payload = {
        subject_id: values.subject_id,
        teacher_id: values.teacher_id,
        branch_id: course.branch_id,
        schedule: [
          {
            start_date: values.start_date,
            time: values.time,
            duration: durationMinutes,
            hours: Math.floor(durationMinutes / 60),
            minutes: durationMinutes % 60,
            recurring: values.recurring || "none",
            number_of_recurrences: values.number_of_recurrences || 1,
            day: moment(values.start_date).format("dddd"),
          },
        ],
      };

      const url = `${API_URL}/classes/add-to-course/${classgroup_id}`;
      await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      message.success("Class(es) added successfully");
      setAddModalVisible(false);
      form.resetFields();
      fetchCourseData();
    } catch (err) {
      console.error("❌ [handleAddClass] Error:", err);
      message.error(err.response?.data?.message || "Failed to add class");
    }
  };


// Calculate statistics
const totalDuration = classes.reduce((sum, cls) => sum + (parseInt(cls.schedule_hour) || 0), 0);

// Get course hours from the course object (NOT from classes array)
const courseHours = course?.course_hour ? parseInt(course.course_hour) : 0;

// course_hour is stored in minutes (same unit as schedule_hour and formatDuration input)
// Remaining = Credit Hours minus (Present + Absent) — both consume credit
const remainingMinutes = courseHours - (attendanceHours.presentMinutes + attendanceHours.absentMinutes);

// Studied hours per subject: a class counts once if any student is Present
// (same rule as the Present card, which uses /attendance/group/:id/hours)
const presentBySubject = Object.values(
  classes.reduce((acc, cls) => {
    const isPresent = (attendanceByClass[cls.classid] || []).some((a) => a.status === 'Present');
    if (!isPresent) return acc;
    const subject = cls.subject_name || 'No subject';
    if (!acc[subject]) acc[subject] = { subject, minutes: 0, count: 0 };
    acc[subject].minutes += parseInt(cls.schedule_hour) || 0;
    acc[subject].count += 1;
    return acc;
  }, {})
).sort((a, b) => b.minutes - a.minutes);

const openAttendanceDrawer = (record) => {
  setAttendanceClass(record);
  const existing = attendanceByClass[record.classid] || [];
  const draft = {};
  registeredStudents.forEach((s) => {
    const saved = existing.find((r) => r.student_id === s.student_id);
    draft[s.student_id] = saved
      ? {
          attendance_id: saved.attendance_id,
          status: saved.status,
          comment: saved.comment || '',
          homework: saved.homework || 'N/A',
        }
      : { attendance_id: null, status: undefined, comment: '', homework: 'N/A' };
  });
  setAttendanceDraft(draft);
  setAttendanceDrawerOpen(true);
};

const updateDraft = (studentId, field, value) => {
  setAttendanceDraft((prev) => ({
    ...prev,
    [studentId]: { ...prev[studentId], [field]: value },
  }));
};

const handleSaveAttendanceRow = async (studentId) => {
  const row = attendanceDraft[studentId];
  if (!row?.status) {
    message.warning('Please select a status');
    return;
  }
  setSavingAttendance(true);
  try {
    const headers = { Authorization: `Bearer ${token}` };
    if (row.attendance_id) {
      await axios.put(
        `${API_URL}/attendance/${row.attendance_id}`,
        { status: row.status, comment: row.comment, homework: row.homework },
        { headers }
      );
    } else {
      await axios.post(
        `${API_URL}/attendance`,
        {
          groupid,
          classid: attendanceClass.classid,
          attendance: [{
            student_id: studentId,
            status: row.status,
            comment: row.comment,
            homework: row.homework,
          }],
        },
        { headers }
      );
    }
    message.success('Attendance saved');
    await fetchCourseData();
  } catch (err) {
    console.error('Failed to save attendance:', err);
    message.error(err.response?.data?.message || 'Failed to save attendance');
  } finally {
    setSavingAttendance(false);
  }
};

// ✨ NEW: Open the bulk attendance modal with every registered student ticked
const openBulkAttendance = () => {
  setBulkAttendanceStudentIds(registeredStudents.map((s) => s.student_id));
  setBulkAttendanceOpen(true);
};

// ✨ NEW: Apply one attendance status to the chosen students across every
// selected class. The POST /attendance endpoint upserts on (student_id, classid),
// so a single request per class covers both new and existing records.
const handleBulkAttendanceSubmit = async () => {
  if (!bulkAttendanceStatus) {
    message.warning('Please select a status');
    return;
  }
  if (registeredStudents.length === 0) {
    message.warning('No students are registered to this course');
    return;
  }

  const chosenStudents = registeredStudents.filter((s) =>
    bulkAttendanceStudentIds.includes(s.student_id)
  );

  if (chosenStudents.length === 0) {
    message.warning('Please select at least one student');
    return;
  }

  const headers = { Authorization: `Bearer ${token}` };
  const commentProvided = bulkAttendanceComment.trim() !== '';
  const homeworkProvided = bulkAttendanceHomework !== undefined;

  // Build one request per class, skipping any class where nothing is left to write
  // (which is what 'fill' mode does once a student already has a record).
  const requests = [];
  selectedRowKeys.forEach((classid) => {
    const existing = attendanceByClass[classid] || [];

    const attendance = chosenStudents
      .map((student) => {
        const saved = existing.find((r) => r.student_id === student.student_id);
        if (bulkAttendanceMode === 'fill' && saved) return null;

        // Blank comment / unset homework means "keep whatever is already there",
        // so the upsert can't quietly wipe notes taken earlier.
        return {
          student_id: student.student_id,
          status: bulkAttendanceStatus,
          comment: commentProvided ? bulkAttendanceComment : (saved?.comment || ''),
          homework: homeworkProvided ? bulkAttendanceHomework : (saved?.homework || 'N/A'),
        };
      })
      .filter(Boolean);

    if (attendance.length > 0) {
      requests.push({ classid, attendance });
    }
  });

  if (requests.length === 0) {
    message.info('Nothing to write — those students already have attendance on every selected class');
    return;
  }

  setBulkAttendanceSaving(true);

  const results = await Promise.allSettled(
    requests.map(({ classid, attendance }) =>
      axios.post(`${API_URL}/attendance`, { groupid, classid, attendance }, { headers })
    )
  );

  setBulkAttendanceSaving(false);

  const failed = results.filter((r) => r.status === 'rejected');
  if (failed.length > 0) {
    console.error('❌ [handleBulkAttendanceSubmit] Failures:', failed.map((f) => f.reason));
    message.error(
      `${results.length - failed.length} of ${results.length} classes saved — ${failed.length} failed`
    );
  } else {
    message.success(
      `Marked ${bulkAttendanceStatus} for ${chosenStudents.length} student(s) on ${requests.length} class${requests.length === 1 ? '' : 'es'}`
    );
    setSelectedRowKeys([]);
  }

  setBulkAttendanceOpen(false);
  setBulkAttendanceStatus(undefined);
  setBulkAttendanceHomework(undefined);
  setBulkAttendanceComment('');
  await fetchCourseData();
};

const handleMarkAllPresent = () => {
  setAttendanceDraft((prev) => {
    const next = { ...prev };
    Object.keys(next).forEach((sid) => {
      if (!next[sid].status) next[sid] = { ...next[sid], status: 'Present' };
    });
    return next;
  });
};

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 180,
      render: (date, record) => {
        const isEditing = editingDateId === record.classid && !isScheduleLocked(record);

        if (!isEditing) {
          return (
            <Space>
              <Button
                type="link"
                icon={<CalendarOutlined />}
                onClick={() => handleEditDateClick(record.classid, record.date)}
                disabled={isScheduleLocked(record)}
                title={isScheduleLocked(record) ? LOCKED_TITLE : undefined}
                style={{ padding: 0, height: "auto" }}
              />
              <Text strong>{moment(date).format("DD MMM YYYY")}</Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <input
              type="date"
              value={editedDate}
              onChange={(e) => setEditedDate(e.target.value)}
              style={{
                width: "100%",
                padding: "4px 11px",
                border: "1px solid #d9d9d9",
                borderRadius: "2px",
                fontSize: "14px",
              }}
            />
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveDateClick(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingDateId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      title: "Day",
      dataIndex: "schedule_day",
      key: "schedule_day",
      width: 100,
      render: (day, record) => (
        <Tag color="blue">
          {day || moment(record.date).format("dddd")}
        </Tag>
      ),
    },
    {
      title: "Time",
      key: "time",
      width: 180,
      render: (_, record) => {
        const isEditing = editingTimeId === record.classid && !isScheduleLocked(record);

        if (!isEditing) {
          const start = moment(record.schedule_time, "HH:mm:ss").format("HH:mm");
          const end = moment(start, "HH:mm")
            .add(record.schedule_hour, "minutes")
            .format("HH:mm");

          return (
            <Space>
              <Button
                type="link"
                icon={<ClockCircleOutlined />}
                onClick={() => handleEditTimeClick(record.classid, record.schedule_time)}
                disabled={isScheduleLocked(record)}
                title={isScheduleLocked(record) ? LOCKED_TITLE : undefined}
                style={{ padding: 0, height: "auto" }}
              />
              <Text>{start} - {end}</Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Change start time only</Text>
            <Select
              value={editedTime}
              onChange={setEditedTime}
              style={{ width: "100%" }}
              options={timeOptions}
              showSearch
            />
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveTime(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingTimeId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Hours",
      dataIndex: "schedule_hour",
      key: "schedule_hour",
      width: 150,
      render: (scheduleHour, record) => {
        const isEditing = editingHourId === record.classid && !isScheduleLocked(record);

        if (!isEditing) {
          return (
            <Space>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditHourClick(record.classid, scheduleHour)}
                disabled={isScheduleLocked(record)}
                title={isScheduleLocked(record) ? LOCKED_TITLE : undefined}
                style={{ padding: 0, height: "auto" }}
              />
              <Text style={{ color: '#555', fontWeight: 500 }}>
                {formatDuration(scheduleHour)}
              </Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              value={editedHour}
              onChange={setEditedHour}
              style={{ width: "100%" }}
              options={durationOptions}
            />
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveHour(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingHourId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Subject",
      dataIndex: "subject_name",
      key: "subject_name",
      width: 200,
      render: (text, record) => {
        const isEditing = editingSubjectId === record.classid;

        if (!isEditing) {
          return (
            <Space>
              <Button
                type="link"
                icon={<BookOutlined />}
                onClick={() => handleEditSubjectClick(record.classid, record.subject_id)}
                style={{ padding: 0, height: "auto" }}
              />
              <Text>{text || "Not assigned"}</Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              value={editedSubjectId}
              onChange={setEditedSubjectId}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {subjects.map((subject) => (
                <Option key={subject.subjectid} value={subject.subjectid}>
                  {subject.title}
                </Option>
              ))}
            </Select>
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveSubjectClick(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingSubjectId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Teacher",
      dataIndex: "nickname",
      key: "nickname",
      width: 200,
      render: (name, record) => {
        const isEditing = editingTeacherId === record.classid;

        if (!isEditing) {
          return (
            <Space>
              <Button
                type="link"
                icon={<UserOutlined />}
                onClick={() => handleEditTeacherClick(record.classid, record.teacher_id)}
                style={{ padding: 0, height: "auto" }}
              />
              <Text>{name || 'Not assigned'}</Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              value={editedTeacherId}
              onChange={setEditedTeacherId}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            >
              {teachers.map((teacher) => (
                <Option
                  key={teacher.userid}
                  value={teacher.userid}
                  label={`${teacher.nickname}`}
                >
                  {teacher.nickname} 
                </Option>
              ))}
            </Select>
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveTeacherClick(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingTeacherId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      width: 160,
      render: (platform, record) => {
        const isEditing = editingPlatformId === record.classid;

        const PLATFORM_OPTIONS = [
          { value: "online", label: "Online", icon: <VideoCameraOutlined /> },
          { value: "onsite", label: "Onsite", icon: <EnvironmentOutlined /> },
          { value: "athome", label: "At Home", icon: <HomeOutlined /> },
        ];

        const getPlatformConfig = (p) => {
          switch (p) {
            case "online":
              return { text: "Online", icon: <VideoCameraOutlined />, bg: "#E6F4FF", border: "#91CAFF", color: "#0958D9" };
            case "onsite":
              return { text: "Onsite", icon: <EnvironmentOutlined />, bg: "#F6FFED", border: "#B7EB8F", color: "#389E0D" };
            case "athome":
              return { text: "At Home", icon: <HomeOutlined />, bg: "#FFF7E6", border: "#FFD591", color: "#D48806" };
            default:
              return { text: "-", icon: <QuestionOutlined />, bg: "#F0F0F0", border: "#D9D9D9", color: "#595959" };
          }
        };

        const config = getPlatformConfig(platform);

        // 🔵 VIEW MODE
        if (!isEditing) {
          return (
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "4px 8px",
              borderRadius: "6px",
              fontSize: "12px",
              lineHeight: 1,
              backgroundColor: config.bg,
              border: `1px solid ${config.border}`,
              color: config.color,
              cursor: "pointer",
            }}
            onClick={() => handleEditPlatformClick(record.classid, platform)}
            >
              {config.icon} {config.text}
            </span>
          );
        }

        // ✏️ EDIT MODE
        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              style={{ width: "100%" }}
              value={editedPlatform}
              onChange={setEditedPlatform}
            >
              {PLATFORM_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.icon} {opt.label}
                </Option>
              ))}
            </Select>

            <Space>
              <Button type="primary" size="small" onClick={() => handleSavePlatformClick(record.classid)}>
                Save
              </Button>
              <Button size="small" onClick={() => setEditingPlatformId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Attendance",
      key: "attendance",
      width: 240,
      render: (_, record) => {
        const records = attendanceByClass[record.classid] || [];
        const colorFor = (status) => {
          if (status === 'Present') return 'green';
          if (status === 'Late') return 'orange';
          if (status === 'Absent') return 'red';
          if (status === 'To be rescheduled') return 'purple';
          return 'default';
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {records.length === 0 ? (
              <Text type="secondary" style={{ fontSize: 12 }}>No attendance taken</Text>
            ) : (
              records.map((a) => (
                <Tag
                  key={a.attendance_id}
                  color={colorFor(a.status)}
                  style={{ fontSize: 11, margin: 0, whiteSpace: 'normal' }}
                >
                  {a.nickname || a.firstname || `#${a.student_id}`} — {a.status}
                </Tag>
              ))
            )}
            <Button
              type="link"
              size="small"
              icon={<TeamOutlined />}
              onClick={() => openAttendanceDrawer(record)}
              style={{ padding: 0, height: 'auto', alignSelf: 'flex-start' }}
            >
              {records.length === 0 ? 'Take Attendance' : 'Edit'}
            </Button>
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 80,
      render: (_, record) => (
        <Button
          danger
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteClass(record.classid)}
        />
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f2f5' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <Header />
        <div style={{ 
          padding: "24px", 
          marginLeft: '70px', 
          marginTop: '60px',
        }}>
          <Spin spinning={loading}>
            {course && (
              <>
                <Card style={{ marginBottom: 24 }}>
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
                          <BookOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                          {course.class_code}
                        </Title>
                        <Title level={4} style={{ margin: 0, color: '#666', fontWeight: 'normal' }}>
                          {course.subject_name}
                        </Title>
                      </div>
                      <Space>
                        <Tag color={course.isprivate ? "blue" : "green"} style={{ fontSize: '14px', padding: '4px 12px' }}>
                          {course.isprivate ? "Private" : "Group"}
                        </Tag>
                        <Tag color="purple" style={{ fontSize: '14px', padding: '4px 12px' }}>
                          {course.platform}
                        </Tag>
                      </Space>
                    </div>

                    <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} style={{ flex: '1 1 0', minWidth: 180 }}>
                      <Card size="small" style={{ background: '#fff1f0', border: '1px solid #ffccc7' }}>
                        <Statistic
                          title="Credit Hours"
                          value={formatDuration(courseHours)}
                          prefix={<ClockCircleOutlined />}
                          valueStyle={{ color: '#cf1322', fontSize: '22px' }}
                        />
                      </Card>
                    </Col>

                    <Col xs={24} sm={12} md={8} style={{ flex: '1 1 0', minWidth: 180 }}>
                      <Card size="small" style={{ background: '#fffbe6', border: '1px solid #ffe58f' }}>
                        <Statistic
                          title="Actual Hours"
                          value={formatDuration(Math.max(0, totalDuration - attendanceHours.toBeRescheduledMinutes))}
                          prefix={<ClockCircleOutlined />}
                          valueStyle={{ color: '#d48806', fontSize: '22px' }}
                        />
                      </Card>
                    </Col>

                    <Col xs={24} sm={12} md={8} style={{ flex: '1 1 0', minWidth: 180 }}>
                      <Card size="small" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                        <Statistic
                          title="Present"
                          value={formatDuration(attendanceHours.presentMinutes)}
                          prefix={<CheckCircleOutlined />}
                          valueStyle={{ color: '#52c41a', fontSize: '22px' }}
                        />
                      </Card>
                    </Col>

                    <Col xs={24} sm={12} md={8} style={{ flex: '1 1 0', minWidth: 180 }}>
                      <Card size="small" style={{ background: '#f9f0ff', border: '1px solid #d3adf7' }}>
                        <Statistic
                          title="To be rescheduled"
                          value={formatDuration(attendanceHours.toBeRescheduledMinutes)}
                          prefix={<ClockCircleOutlined />}
                          valueStyle={{ color: '#722ed1', fontSize: '22px' }}
                        />
                      </Card>
                    </Col>

                    <Col xs={24} sm={12} md={8} style={{ flex: '1 1 0', minWidth: 180 }}>
                      <Card size="small" style={{ background: '#fff2f0', border: '1px solid #ffccc7' }}>
                        <Statistic
                          title="Absent"
                          value={formatDuration(attendanceHours.absentMinutes)}
                          prefix={<CloseCircleOutlined />}
                          valueStyle={{ color: '#ff4d4f', fontSize: '22px' }}
                        />
                      </Card>
                    </Col>

                    <Col xs={24} sm={12} md={8} style={{ flex: '1 1 0', minWidth: 180 }}>
                      <Card
                        size="small"
                        style={{
                          background: remainingMinutes >= 0 ? '#e6f7ff' : '#fff1f0',
                          border: remainingMinutes >= 0 ? '1px solid #91d5ff' : '1px solid #ffccc7',
                        }}
                      >
                        <Statistic
                          title="Remaining Hours"
                          value={`${remainingMinutes >= 0 ? '' : '- '}${formatDuration(Math.abs(remainingMinutes))}`}
                          prefix={remainingMinutes >= 0 ? <ClockCircleOutlined /> : <WarningOutlined />}
                          valueStyle={{
                            color: remainingMinutes >= 0 ? '#1890ff' : '#cf1322',
                            fontSize: '22px',
                          }}
                          suffix={remainingMinutes < 0 ? '(Over)' : ''}
                        />
                      </Card>
                    </Col>

                    <Col xs={24}>
                      <Card size="small" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                        <Text type="secondary">Studied Hours by Subject (Present)</Text>
                        {presentBySubject.length === 0 ? (
                          <div><Text type="secondary">No present classes yet</Text></div>
                        ) : (
                          <div style={{ marginTop: 6 }}>
                            {presentBySubject.map((row) => (
                              <div
                                key={row.subject}
                                style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0' }}
                              >
                                <Text>{row.subject}</Text>
                                <Text strong style={{ color: '#389e0d', whiteSpace: 'nowrap' }}>
                                  {formatDuration(row.minutes)}
                                  <Text type="secondary" style={{ fontWeight: 'normal' }}>
                                    {' '}({row.count} {row.count === 1 ? 'class' : 'classes'})
                                  </Text>
                                </Text>
                              </div>
                            ))}
                          </div>
                        )}
                      </Card>
                    </Col>

                    <Col xs={24}>
                      <Card size="small" style={{ background: '#fff7e6', border: '1px solid #ffd591' }}>
                        <Statistic
                          title="Period"
                          value={`${moment(course.start_date).format("DD MMM")} - ${moment(course.end_date).format("DD MMM YYYY")}`}
                          valueStyle={{ color: '#fa8c16', fontSize: '16px' }}
                        />
                      </Card>
                    </Col>
                  </Row>
                  </Space>
                </Card>

                <Card style={{ marginBottom: 24 }}>
                  <Space>
                      <Button 
                      icon={<ArrowLeftOutlined />} 
                      onClick={() => {
                        // Go back one step (keeps the list's search); fall back if opened directly
                        if (window.history.state?.idx > 0) navigate(-1);
                        else navigate('/adminpanel?tab=courses');
                      }}
                      size="large"
                    >
                      Back
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setAddModalVisible(true)}
                      size="large"
                    >
                      Add Class
                    </Button>
                    <Button
                      icon={<FilePdfOutlined />}
                      onClick={() => setPdfPreviewVisible(true)}
                      size="large"
                      style={{ 
                        background: 'blue', 
                        borderColor: 'blue',
                        color: '#fff'
                      }}
                    >
                      Download Calendar
                    </Button>
                    {selectedRowKeys.length > 0 && (
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={handleBulkDelete}
                        size="large"
                      >
                        Delete {selectedRowKeys.length} Selected
                      </Button>
                    )}
                  </Space>
                </Card>

                <div id="course-table-container">
                <Card 
                  title={<Text strong style={{ fontSize: '16px' }}>Class Schedule</Text>}
                  extra={
                    <Space>
                      {selectedRowKeys.length > 0 && (
                        <>
                          <Button
                            icon={<UserOutlined />}
                            onClick={() => { setBulkEditField('teacher_id'); setBulkEditValue(null); }}
                            size="middle"
                          >
                            Change Teacher ({selectedRowKeys.length})
                          </Button>
                          <Button
                            icon={<BookOutlined />}
                            onClick={() => { setBulkEditField('subjectid'); setBulkEditValue(null); }}
                            size="middle"
                          >
                            Change Subject ({selectedRowKeys.length})
                          </Button>
                          <Button
                            icon={<CheckCircleOutlined />}
                            onClick={openBulkAttendance}
                            size="middle"
                          >
                            Attendance ({selectedRowKeys.length})
                          </Button>
                        </>
                      )}
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => setPlatformModalVisible(true)}
                        size="middle"
                        style={{ 
                          background: "#722ed1", 
                          borderColor: "#722ed1",
                          color: "#fff",
                        }}
                      >
                        Add/Update Platform
                      </Button>
                    </Space>
                  }
                >
                  <Table
                    rowSelection={{
                      selectedRowKeys,
                      onChange: (keys) => setSelectedRowKeys(keys),
                    }}
                    columns={columns}
                    dataSource={classes}
                    rowKey="classid"
                    bordered
                    pagination={{
                      pageSize: 20,
                      showSizeChanger: true,
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} classes`
                    }}
                    rowClassName={(record) => {
                      const classDateTime = moment(`${record.date} ${record.schedule_time}`, 'YYYY-MM-DD HH:mm:ss');
                      return classDateTime.isBefore(moment()) ? 'class-ended-row' : '';
                    }}
                  />
                </Card>
              </div>
              </>
            )}
          </Spin>
        </div>
      </div>

      {/* Add Class Modal - Keep the same */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            <Text strong>Add Class to Course</Text>
          </Space>
        }
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        onOk={handleAddClass}
        okText="Add Class"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item
            name="subject_id"
            label="Subject"
            rules={[{ required: true, message: "Please select a subject" }]}
          >
            <Select
              placeholder="Select subject"
              style={{ width: "100%" }}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={subjects.map(sub => ({
                value: sub.subjectid,
                label: sub.title,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="teacher_id"
            label="Teacher"
            rules={[{ required: true, message: "Please select a teacher" }]}
          >
            <Select
              placeholder="Select teacher"
              style={{ width: "100%" }}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={teachers.map(t => ({
                value: t.userid,
                label: `${t.firstname} ${t.lastname}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="start_date"
            label="Start Date"
            rules={[{ required: true, message: "Please select a date" }]}
          >
            <input
              type="date"
              style={{
                width: '100%',
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                padding: '8px 12px',
                fontSize: 14,
              }}
              onChange={(e) => form.setFieldsValue({ start_date: e.target.value })}
            />
          </Form.Item>

          <Form.Item
            name="time"
            label="Start Time"
            rules={[{ required: true, message: "Please select start time" }]}
          >
            <Select
              placeholder="Select start time"
              showSearch
              style={{ width: '100%' }}
              options={timeOptions}
            />
          </Form.Item>

          <Form.Item
            name="duration"
            label="Hours"
            rules={[{ required: true, message: "Please select duration" }]}
          >
            <Select
              placeholder="Select duration"
              style={{ width: '100%' }}
              options={durationOptions}
            />
          </Form.Item>

          <Form.Item name="recurring" label="Recurrence" initialValue="none">
            <Select placeholder="None">
              <Option value="none">None (single class)</Option>
              <Option value="daily">Daily</Option>
              <Option value="weekly">Weekly</Option>
              <Option value="bi-weekly">Bi-weekly</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="number_of_recurrences"
            label="Number of Occurrences"
            initialValue={1}
          >
            <InputNumber min={1} max={50} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
  title="Update Platform for Course"
  open={platformModalVisible}
  onCancel={() => {
    setPlatformModalVisible(false);
    setSelectedCoursePlatform(null);
  }}
  footer={[
    <Button key="cancel" onClick={() => setPlatformModalVisible(false)}>
      Cancel
    </Button>,
    <Button
      key="save"
      type="primary"
      disabled={!selectedCoursePlatform}
      onClick={async () => {
        Modal.confirm({
          title: "Are you sure?",
          content: (
            <>
              This will <strong>overwrite the platform for ALL classes</strong>{" "}
              in this course.
            </>
          ),
          okText: "Yes, Update All",
          cancelText: "No",
          onOk: async () => {
            try {
              await axios.put(
                `${API_URL}/class-groups/update-platform/${groupid}`,
                { platform: selectedCoursePlatform },
                { headers: { Authorization: `Bearer ${token}` } }
              );

              message.success("Platform updated for entire course");
              setPlatformModalVisible(false);
              setSelectedCoursePlatform(null);
              fetchCourseData();
            } catch (error) {
              console.error(error);
              message.error("Failed to update platform");
            }
          },
        });
      }}
    >
      Save
    </Button>,
  ]}
>
  <p style={{ fontWeight: 600, marginBottom: 10 }}>
    Select platform (this applies to all classes in this course)
  </p>

  <Select
    style={{ width: "100%" }}
    placeholder="Choose platform"
    value={selectedCoursePlatform}
    onChange={(value) => setSelectedCoursePlatform(value)}
  >
    <Select.Option value="online">
      <VideoCameraOutlined /> Online
    </Select.Option>
    <Select.Option value="onsite">
      <EnvironmentOutlined /> Onsite
    </Select.Option>
    <Select.Option value="athome">
      <HomeOutlined /> At Home
    </Select.Option>
  </Select>

  <div
    style={{
      marginTop: 16,
      padding: 12,
      borderRadius: 6,
      background: "#fff7e6",
      border: "1px solid #ffd591",
      color: "#ad6800",
    }}
  >
    ⚠️ This will overwrite platform for all classes in the same course.
  </div>
</Modal>
<CourseSchedulePdfModal
  open={pdfPreviewVisible}
  onClose={() => setPdfPreviewVisible(false)}
  course={course}
  classes={classes}
  attendanceByClass={attendanceByClass}
  formatDuration={formatDuration}
/>

<Drawer
  title={
    attendanceClass ? (
      <Space>
        <TeamOutlined />
        <span>
          Attendance — {moment(attendanceClass.date).format('DD MMM YYYY')} {moment(attendanceClass.schedule_time, 'HH:mm:ss').format('HH:mm')}
        </span>
      </Space>
    ) : 'Attendance'
  }
  open={attendanceDrawerOpen}
  onClose={() => setAttendanceDrawerOpen(false)}
  width={720}
>
  {registeredStudents.length === 0 ? (
    <Empty description="No students registered to this course yet" />
  ) : (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text strong>{registeredStudents.length} student(s)</Text>
        <Button size="small" icon={<CheckCircleOutlined />} onClick={handleMarkAllPresent}>
          Mark All Present
        </Button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {registeredStudents.map((s) => {
          const row = attendanceDraft[s.student_id] || { status: undefined, comment: '', homework: 'N/A' };
          const isSaved = !!row.attendance_id;
          return (
            <Card key={s.student_id} size="small" bodyStyle={{ padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Space>
                  <UserOutlined />
                  <Text strong>{s.nickname || s.student_name}</Text>
                  {isSaved && <Tag color="blue">Saved</Tag>}
                </Space>
                <Button
                  type="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  loading={savingAttendance}
                  disabled={!row.status}
                  onClick={() => handleSaveAttendanceRow(s.student_id)}
                >
                  {isSaved ? 'Update' : 'Save'}
                </Button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 12 }}>
                <Select
                  placeholder="Status"
                  value={row.status}
                  onChange={(v) => updateDraft(s.student_id, 'status', v)}
                >
                  <Select.Option value="Present">Present</Select.Option>
                  <Select.Option value="Late">Late ≤ 15 M</Select.Option>
                  <Select.Option value="Absent">Absent</Select.Option>
                  <Select.Option value="To be rescheduled">To be rescheduled</Select.Option>
                </Select>

                <Select
                  placeholder="Homework"
                  value={row.homework}
                  onChange={(v) => updateDraft(s.student_id, 'homework', v)}
                >
                  <Select.Option value="YES">YES</Select.Option>
                  <Select.Option value="NO">NO</Select.Option>
                  <Select.Option value="N/A">N/A</Select.Option>
                </Select>

                <Input.TextArea
                  placeholder="Comment"
                  value={row.comment}
                  onChange={(e) => updateDraft(s.student_id, 'comment', e.target.value)}
                  rows={1}
                  autoSize={{ minRows: 1, maxRows: 3 }}
                />
              </div>
            </Card>
          );
        })}
      </div>
    </>
  )}
</Drawer>

{/* ✨ NEW: Bulk attendance on the selected classes */}
<Modal
  title={
    <Space>
      <CheckCircleOutlined />
      Bulk Attendance
      <Text type="secondary" style={{ fontWeight: 'normal' }}>
        ({selectedRowKeys.length} class{selectedRowKeys.length === 1 ? '' : 'es'})
      </Text>
    </Space>
  }
  open={bulkAttendanceOpen}
  onCancel={() => setBulkAttendanceOpen(false)}
  onOk={handleBulkAttendanceSubmit}
  okText="Save Attendance"
  confirmLoading={bulkAttendanceSaving}
  okButtonProps={{
    disabled:
      !bulkAttendanceStatus ||
      registeredStudents.length === 0 ||
      bulkAttendanceStudentIds.length === 0,
  }}
  destroyOnClose
>
  {registeredStudents.length === 0 ? (
    <Empty description="No students are registered to this course yet" />
  ) : (
    <>
      <Text type="secondary">
        Applies to {bulkAttendanceStudentIds.length} of {registeredStudents.length} student(s) on
        every selected class.
      </Text>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
        {registeredStudents.length > 1 && (
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Text strong>Students</Text>
              <Space size={4}>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  onClick={() =>
                    setBulkAttendanceStudentIds(registeredStudents.map((s) => s.student_id))
                  }
                >
                  Select all
                </Button>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  onClick={() => setBulkAttendanceStudentIds([])}
                >
                  Clear
                </Button>
              </Space>
            </div>
            <Select
              mode="multiple"
              placeholder="Select students"
              value={bulkAttendanceStudentIds}
              onChange={setBulkAttendanceStudentIds}
              style={{ width: '100%', marginTop: 4 }}
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            >
              {registeredStudents.map((student) => (
                <Option
                  key={student.student_id}
                  value={student.student_id}
                  label={student.nickname || student.student_name}
                >
                  {student.nickname || student.student_name}
                </Option>
              ))}
            </Select>
          </div>
        )}

        <div>
          <Text strong>Status</Text>
          <Select
            placeholder="Select a status"
            value={bulkAttendanceStatus}
            onChange={setBulkAttendanceStatus}
            style={{ width: '100%', marginTop: 4 }}
          >
            <Option value="Present">Present</Option>
            <Option value="Late">Late ≤ 15 M</Option>
            <Option value="Absent">Absent</Option>
            <Option value="To be rescheduled">To be rescheduled</Option>
          </Select>
        </div>

        <div>
          <Text strong>What to write</Text>
          <Select
            value={bulkAttendanceMode}
            onChange={setBulkAttendanceMode}
            style={{ width: '100%', marginTop: 4 }}
          >
            <Option value="overwrite">
              Overwrite attendance already taken
            </Option>
            <Option value="fill">
              Only where the student has no attendance yet
            </Option>
          </Select>
        </div>

        <div>
          <Text strong>Homework</Text>
          <Select
            placeholder="Leave unchanged"
            value={bulkAttendanceHomework}
            onChange={setBulkAttendanceHomework}
            style={{ width: '100%', marginTop: 4 }}
            allowClear
          >
            <Option value="YES">YES</Option>
            <Option value="NO">NO</Option>
            <Option value="N/A">N/A</Option>
          </Select>
        </div>

        <div>
          <Text strong>Comment</Text>
          <Input.TextArea
            placeholder="Leave blank to keep existing comments"
            value={bulkAttendanceComment}
            onChange={(e) => setBulkAttendanceComment(e.target.value)}
            rows={2}
            style={{ marginTop: 4 }}
          />
        </div>
      </div>
    </>
  )}
</Modal>

{/* ✨ NEW: Bulk change teacher / subject on the selected classes */}
<Modal
  title={
    <Space>
      {bulkEditField === 'teacher_id' ? <UserOutlined /> : <BookOutlined />}
      {bulkEditField === 'teacher_id' ? 'Change Teacher' : 'Change Subject'}
      <Text type="secondary" style={{ fontWeight: 'normal' }}>
        ({selectedRowKeys.length} class{selectedRowKeys.length === 1 ? '' : 'es'})
      </Text>
    </Space>
  }
  open={bulkEditField !== null}
  onCancel={() => { setBulkEditField(null); setBulkEditValue(null); }}
  onOk={handleBulkEditSubmit}
  okText={`Apply to ${selectedRowKeys.length} class${selectedRowKeys.length === 1 ? '' : 'es'}`}
  confirmLoading={bulkSaving}
  okButtonProps={{ disabled: bulkEditValue === null || bulkEditValue === undefined }}
  destroyOnClose
>
  <Text type="secondary">
    This overwrites the {bulkEditField === 'teacher_id' ? 'teacher' : 'subject'} on every selected
    class and records a change log entry for each one.
  </Text>

  <div style={{ marginTop: 16 }}>
    {bulkEditField === 'teacher_id' ? (
      <Select
        placeholder="Select a teacher"
        value={bulkEditValue ?? undefined}
        onChange={setBulkEditValue}
        style={{ width: '100%' }}
        showSearch
        optionFilterProp="label"
        filterOption={(input, option) =>
          option.label.toLowerCase().includes(input.toLowerCase())
        }
      >
        {teachers.map((teacher) => (
          <Option key={teacher.userid} value={teacher.userid} label={`${teacher.nickname}`}>
            {teacher.nickname}
          </Option>
        ))}
      </Select>
    ) : (
      <Select
        placeholder="Select a subject"
        value={bulkEditValue ?? undefined}
        onChange={setBulkEditValue}
        style={{ width: '100%' }}
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().includes(input.toLowerCase())
        }
      >
        {subjects.map((subject) => (
          <Option key={subject.subjectid} value={subject.subjectid}>
            {subject.title}
          </Option>
        ))}
      </Select>
    )}
  </div>
</Modal>
    </div>
  );
};

export default withRoleAccess(CoursePage, ["admin", "superadmin"]);
